import { IStringTool } from "../core/IStringTool";

/**
 * Input for origin parser.
 * Supports static values or `random(...)` expressions.
 */
interface OriginInput {
  /** Raw origin string, e.g. `'center'` or `'random(top, bottom)'`. */
  value: string;
}

/**
 * Input for parsing origin to normalized coordinates.
 * Supports formats like `'left top'`, `'center center'`, `'50% 50%'`, `'35% 76%'`.
 */
interface OriginToNormalizedInput {
  /** Raw origin string, e.g. `'left top'`, `'center'`, `'50% 25%'`. */
  value: string;
}

/**
 * Output: normalized origin coordinates (0-1 range).
 */
interface NormalizedOrigin {
  /** Normalized X coordinate (0 = left, 0.5 = center, 1 = right). */
  x: number;
  /** Normalized Y coordinate (0 = top, 0.5 = center, 1 = bottom). */
  y: number;
}

/** Keyword mappings for horizontal axis. */
const HORIZONTAL_KEYWORDS: Record<string, number> = {
  left: 0,
  center: 0.5,
  right: 1,
};

/** Keyword mappings for vertical axis. */
const VERTICAL_KEYWORDS: Record<string, number> = {
  top: 0,
  center: 0.5,
  bottom: 1,
};

/**
 * Tool that parses origin strings.
 * Allows static values like `'center'`, or expressions like `'random(...)'` to select one randomly.
 * Also provides `toNormalized()` method to convert origin strings to `{ x, y }` coordinates.
 */
export default class OriginParserTool implements IStringTool<OriginInput, string> {
  /**
   * @returns Parsed string value (static or randomly chosen).
   */
  process({ value }: OriginInput): string {
    if (!value) return "center";
    const raw = value.trim();

    if (raw.startsWith("random(") && raw.endsWith(")")) {
      const options = raw
        .slice(7, -1)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const index = Math.floor(Math.random() * options.length);
      return options[index];
    }

    return raw;
  }

  /**
   * Parses an origin string to normalized `{ x, y }` coordinates (0-1 range).
   *
   * Supported formats:
   * - Single keyword: `'center'`, `'top'`, `'left'`, etc.
   * - Two keywords: `'left top'`, `'right bottom'`, `'center center'`
   * - Percentages: `'50% 50%'`, `'0% 100%'`, `'35% 76%'`
   * - Mixed: `'left 25%'`, `'50% top'`
   *
   * @param input - The origin string to parse.
   * @returns Normalized `{ x, y }` coordinates.
   */
  toNormalized({ value }: OriginToNormalizedInput): NormalizedOrigin {
    const raw = this.process({ value });
    const parts = raw.toLowerCase().split(/\s+/).filter(Boolean);

    if (parts.length === 0) {
      return { x: 0.5, y: 0.5 };
    }

    if (parts.length === 1) {
      const single = parts[0];
      const parsed = this.parseValue(single);

      // If it's a horizontal keyword, use center for Y
      if (single in HORIZONTAL_KEYWORDS && !(single in VERTICAL_KEYWORDS)) {
        return { x: parsed, y: 0.5 };
      }
      // If it's a vertical keyword, use center for X
      if (single in VERTICAL_KEYWORDS && !(single in HORIZONTAL_KEYWORDS)) {
        return { x: 0.5, y: parsed };
      }
      // 'center' or percentage applies to both
      return { x: parsed, y: parsed };
    }

    // Two parts: determine which is X and which is Y
    const [first, second] = parts;

    // Check if keywords need to be swapped (e.g., "top left" → x=left, y=top)
    const firstIsVerticalOnly = first in VERTICAL_KEYWORDS && !(first in HORIZONTAL_KEYWORDS);
    const secondIsHorizontalOnly = second in HORIZONTAL_KEYWORDS && !(second in VERTICAL_KEYWORDS);

    if (firstIsVerticalOnly || secondIsHorizontalOnly) {
      // Swap: first is Y, second is X
      return {
        x: this.parseValue(second, "horizontal"),
        y: this.parseValue(first, "vertical"),
      };
    }

    // Default order: first is X, second is Y
    return {
      x: this.parseValue(first, "horizontal"),
      y: this.parseValue(second, "vertical"),
    };
  }

  /**
   * Parses a single value (keyword or percentage) to a normalized number.
   */
  private parseValue(val: string, axis?: "horizontal" | "vertical"): number {
    // Check keywords
    if (axis === "horizontal" && val in HORIZONTAL_KEYWORDS) {
      return HORIZONTAL_KEYWORDS[val];
    }
    if (axis === "vertical" && val in VERTICAL_KEYWORDS) {
      return VERTICAL_KEYWORDS[val];
    }
    if (val in HORIZONTAL_KEYWORDS) {
      return HORIZONTAL_KEYWORDS[val];
    }
    if (val in VERTICAL_KEYWORDS) {
      return VERTICAL_KEYWORDS[val];
    }

    // Parse percentage
    if (val.endsWith("%")) {
      const num = parseFloat(val);
      if (!isNaN(num)) {
        return num / 100;
      }
    }

    // Parse plain number (treat as percentage)
    const num = parseFloat(val);
    if (!isNaN(num)) {
      // If it's a small number (0-1), treat as normalized
      // If it's larger, treat as percentage
      return num > 1 ? num / 100 : num;
    }

    // Default to center
    return 0.5;
  }
}
